/** @jsxImportSource react */
import EntityReview from '@react/profile/v2/CommunityReviews/EntityReview';
import { usePrefetch } from '@react/services/api/prefetch';
import Carousel from '@react/shared/components/Carousel';
import NextImage from '@react/shared/components/Image';
import { Link } from '@react/system';
import { Rating } from '@sly/core/types';

interface ReviewsSectionProps {
  reviews: Rating[];
  title?: string;
}

const ReviewsSection = ({ reviews, title }: ReviewsSectionProps) => {
  return (
    <div>
      {title && <h2 className='font-t-l'>{title}</h2>}
      <Carousel className='py-4' itemsQty={reviews?.length} key='Favorites'>
        {reviews.map((review) => (
          <div
            key={review.id}
            className='flex min-h-full flex-col justify-between rounded-lg border border-slate-lighter-90'
          >
            <div className='p-4'>
              <EntityReview
                info={review.info}
                value={review.value}
                comments={review.comments}
                createdAt={review.createdAt}
                showReadMore
                readMoreClickHandler={
                  review?.searchResource?.url &&
                  (() => window.open(review?.searchResource?.url, '_blank'))
                }
              />
            </div>
            {review?.searchResource && (
              <Link
                className='flex flex-row gap-2 bg-slate-lighter-95 p-4 text-black no-underline'
                target='_blank'
                href={review?.searchResource?.url}
                rel='noreferrer noopener'
              >
                <NextImage
                  path={review.searchResource?.gallery?.images[0]?.path}
                  alt={review.searchResource?.name}
                  width={48}
                  height={48}
                  layout='raw'
                  objectFit='fill'
                  className='h-12 w-12 rounded-lg'
                />
                <div className='flex flex-col overflow-hidden text-ellipsis whitespace-nowrap'>
                  <div className='font-t-xxs-azo'>
                    {review.searchResource?.firstLine}
                  </div>
                  <div className='font-b-s overflow-hidden text-ellipsis whitespace-nowrap text-slate-lighter-40'>
                    {review.searchResource?.addressString}
                  </div>
                  <div className='font-b-s overflow-hidden text-ellipsis whitespace-nowrap  text-slate-lighter-40'>
                    {review.searchResource?.secondLine}
                  </div>
                </div>
              </Link>
            )}
          </div>
        ))}
      </Carousel>
    </div>
  );
};

export default ReviewsSection;

interface ReviewsSectionWrapperProps {
  title: string;
  slugs: string;
}

export const ReviewsSectionWrapper = ({
  title,
  slugs,
}: ReviewsSectionWrapperProps) => {
  const { requestInfo } = usePrefetch('getAllReviews', {
    'filter[slug]': slugs,
    include: ['searchResource', 'searchResource.gallery.images'],
  });

  return (
    <ReviewsSection reviews={requestInfo?.normalized ?? []} title={title} />
  );
};
